import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import { ask, resolvePending } from '../ask-user-question/askUserQuestionStore';
import { IconRegistry } from '../registries/IconRegistry';
import type { ToolExecutionContext } from '../types';

import { AddVoiceoverSceneTool } from './AddVoiceoverSceneTool';
import { FindWaveformSceneDesignTool } from './FindWaveformSceneDesignTool';
import { resolveSceneHandle, sceneDisplayId } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import { MediaSearchSourceEnum } from 'js/src/client';
import { ApiService } from 'js/src/client/services.gen';
import { MEDIA_LICENSE_OPTIONS } from 'js/src/data/media';
import type { RootState } from 'js/src/libs/RootState';
import { setIconSouceDataForSceneOperation } from 'js/src/luminary/icon-operations';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const setWaveformStyleSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    waveform_name: z
        .string()
        .optional()
        .describe(
            'The name of a waveform style to apply directly, skipping the picker. Only use when the user already chose a style by name.',
        ),
});
export type SetWaveformStyleArgs = z.infer<typeof setWaveformStyleSchema>;

interface WaveformStyle {
    id: string;
    title: string;
}

/**
 * The curated waveform catalog, registered into the IconRegistry so the ids are
 * usable with it. `addItem` dedupes by UUID, so this returns the same ids the
 * picker's widget registers for the same styles.
 */
async function fetchWaveformStyles(): Promise<WaveformStyle[]> {
    const response = await ApiService.mediaSearchCreate({
        body: {
            q: 'waveform',
            search_source: MediaSearchSourceEnum.WAVEFORMS,
            page: 1,
            count: 20,
            filter: MEDIA_LICENSE_OPTIONS.EDITORIAL + ',' + MEDIA_LICENSE_OPTIONS.STOCK,
            autocorrect: false,
            did_you_mean: false,
        },
    });
    const iconRegistry = IconRegistry.getInstance();
    return response.data.results.map((result) => {
        const id = iconRegistry.addItem(result);
        return { id, title: iconRegistry.getRepresentation(id)?.title ?? id };
    });
}

/**
 * Swaps which waveform Lottie animates inside a scene that already has one (an
 * icon asset with playbackMode 'waveform', e.g. from an audiogram scene design).
 * Called without waveform_name it shows the user a visual picker instead.
 */
export class SetWaveformStyleTool extends VideoStateTool<typeof setWaveformStyleSchema> {
    public static readonly toolName = 'set_waveform_style';

    protected override readonly schema = setWaveformStyleSchema;
    protected override readonly toolName = SetWaveformStyleTool.toolName;
    protected override readonly toolDescription =
        'Change which waveform animation pulses with the voice on a scene that already has a waveform (e.g. an audiogram scene design). Call without waveform_name to show the user a visual picker of the available styles — it waits for their choice and applies it, so no follow-up question is needed. Only pass waveform_name when the user already named a specific style. The scene layout, captions, and colors are unchanged — only the waveform visualization swaps.';

    public override getToolDisplayName(): string {
        return 'Choosing waveform style';
    }

    public get statusIcon(): string {
        return 'graphic_eq';
    }

    public async execute(args: SetWaveformStyleArgs, context: ToolExecutionContext) {
        const { waveform_name } = args;
        const { parentActionGroup } = context;

        const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
            this.luminary,
            this.getState(),
            args,
        );
        const scene = this.luminary.scenes[scene_index];
        const waveformIcon = scene
            .getAssets()
            .find(
                (asset) =>
                    VideoData.isIconAsset(asset) &&
                    asset.playbackMode === VideoData.PLAYBACK_WAVEFORM,
            );
        if (!waveformIcon) {
            return `Error: Scene ${sceneShortId} has no waveform to restyle. Apply a waveform (audiogram) scene design first — e.g. ${AddVoiceoverSceneTool.toolName} with cover_entire_video and the design from ${FindWaveformSceneDesignTool.toolName}.`;
        }

        // Only the by-name path needs the catalog here; the picker's widget
        // loads it itself and hands back the registry id of the chosen style.
        if (waveform_name) {
            let waveformStyles: WaveformStyle[];
            try {
                waveformStyles = await fetchWaveformStyles();
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error occurred';
                return `Error fetching waveform styles: ${errorMessage}`;
            }
            if (waveformStyles.length === 0) {
                return 'Error: No waveform styles are available.';
            }
            const requested = waveform_name.trim().toLowerCase();
            const match = waveformStyles.find((style) => style.title.toLowerCase() === requested);
            if (!match) {
                return `Error: No waveform style named "${waveform_name}". Available styles: ${waveformStyles
                    .map((style) => style.title)
                    .join(', ')}`;
            }
            return this.applyStyle(scene.id, waveformIcon.id, match, parentActionGroup);
        }

        return this.pickAndApplyStyle(scene.id, waveformIcon.id, context);
    }

    private async pickAndApplyStyle(
        sceneId: string,
        waveformIconId: string,
        context: ToolExecutionContext,
    ) {
        const abortSignal = context.abortSignal;
        if (abortSignal?.aborted) {
            return 'Waveform style picker cancelled before it was shown. The current style is kept.';
        }
        const onAbort = () => resolvePending({ status: 'dismissed' });
        abortSignal?.addEventListener('abort', onAbort);

        let result;
        try {
            // The widget loads the catalog itself, hence no options.
            result = await ask([
                {
                    question: 'Which waveform style?',
                    type: 'waveform',
                    options: [],
                },
            ]);
        } finally {
            abortSignal?.removeEventListener('abort', onAbort);
        }

        if (result.status === 'dismissed') {
            return 'The user dismissed the waveform style picker; the current style is kept. Do not ask again — continue with the video.';
        }

        const chosen = result.answers[0]?.resolved;
        if (!chosen?.name || !chosen.id) {
            return 'Success: The user kept the default waveform style.';
        }

        return this.applyStyle(
            sceneId,
            waveformIconId,
            { id: chosen.id, title: chosen.name },
            context.parentActionGroup,
        );
    }

    private async applyStyle(
        sceneId: string,
        waveformIconId: string,
        style: WaveformStyle,
        parentActionGroup: string,
    ) {
        const sceneShortId = sceneDisplayId(this.luminary, sceneId);
        const iconSourceData = IconRegistry.getInstance().getIconSourceData(style.id);
        if (!iconSourceData) {
            return `Error: Could not load the "${style.title}" waveform style.`;
        }

        try {
            const { setIconSouceDataForScene } = makeOperationsCallable(
                { setIconSouceDataForSceneOperation },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );
            const updatedAsset = await setIconSouceDataForScene(
                sceneId,
                iconSourceData,
                { targetAssetId: waveformIconId, isWaveform: true },
                false,
                parentActionGroup,
            );
            if (!updatedAsset) {
                return `Error: Failed to apply the "${style.title}" waveform style to scene ${sceneShortId}.`;
            }
            return `Success: Set the waveform style on scene ${sceneShortId} to "${style.title}".`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting waveform style: ${errorMessage}`;
        }
    }
}
